// =============================================================================
// MeuDriver — Q1: Editor de Disponibilidade Semanal Recorrente
// Permite ao instrutor definir horários padrão por dia da semana.
// =============================================================================

import { useState, useEffect } from 'react'
import { Plus, Trash2, Clock, Loader2 } from 'lucide-react'
import { useMyInstructorId } from '@/hooks/useInstructorProfile'
import {
  useInstructorWeeklyAvailability,
  useSetWeeklyAvailability,
  type WeeklySlotInput,
} from '@/hooks/useWeeklyAvailability'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

// ─── Constantes ───────────────────────────────────────────────────────────────

const DAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
const DAY_SHORT  = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

function buildHourOptions(): string[] {
  return Array.from({ length: 17 }, (_, i) => {
    const h = i + 6
    return `${String(h).padStart(2, '0')}:00`
  })
}

const HOUR_OPTIONS = buildHourOptions()

// ─── Tipos locais ─────────────────────────────────────────────────────────────

interface DaySlot {
  start_time: string
  end_time: string
}

type DayState = {
  enabled: boolean
  slots: DaySlot[]
}

type WeekState = Record<number, DayState>

function defaultWeekState(): WeekState {
  return Object.fromEntries(
    [0, 1, 2, 3, 4, 5, 6].map((d) => [
      d,
      { enabled: false, slots: [{ start_time: '08:00', end_time: '18:00' }] },
    ]),
  )
}

// ─── TimeSelect ───────────────────────────────────────────────────────────────

function TimeSelect({
  value,
  onChange,
  options,
  label,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  label: string
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
        aria-label={label}
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  )
}

// ─── DayRow ───────────────────────────────────────────────────────────────────

interface DayRowProps {
  dow: number
  state: DayState
  onChange: (state: DayState) => void
}

function DayRow({ dow, state, onChange }: DayRowProps) {
  function toggleDay(enabled: boolean) {
    onChange({ ...state, enabled })
  }

  function updateSlot(idx: number, field: 'start_time' | 'end_time', value: string) {
    const slots = state.slots.map((s, i) => (i === idx ? { ...s, [field]: value } : s))
    onChange({ ...state, slots })
  }

  function addSlot() {
    const last = state.slots[state.slots.length - 1]
    const newStart = last ? last.end_time : '08:00'
    // Tenta uma hora após o término do último slot
    const newStartIdx = HOUR_OPTIONS.indexOf(newStart)
    const safeStart = newStartIdx >= 0 && newStartIdx < HOUR_OPTIONS.length - 1
      ? HOUR_OPTIONS[newStartIdx]
      : HOUR_OPTIONS[0]
    const safeEndIdx = HOUR_OPTIONS.indexOf(safeStart) + 1
    const safeEnd = safeEndIdx < HOUR_OPTIONS.length
      ? HOUR_OPTIONS[safeEndIdx]
      : HOUR_OPTIONS[HOUR_OPTIONS.length - 1]

    onChange({
      ...state,
      slots: [...state.slots, { start_time: safeStart, end_time: safeEnd }],
    })
  }

  function removeSlot(idx: number) {
    onChange({ ...state, slots: state.slots.filter((_, i) => i !== idx) })
  }

  return (
    <div
      className={cn(
        'rounded-xl border transition-colors',
        state.enabled
          ? 'border-teal-200 bg-teal-50/30 dark:border-teal-800 dark:bg-teal-950/20'
          : 'border-gray-100 bg-gray-50/50 dark:border-gray-800 dark:bg-gray-900/30',
      )}
    >
      {/* Header do dia */}
      <div className="flex items-center gap-3 px-4 py-3">
        <Switch
          id={`day-${dow}`}
          checked={state.enabled}
          onCheckedChange={toggleDay}
          data-testid={`weekly-avail-toggle-${dow}`}
        />
        <Label
          htmlFor={`day-${dow}`}
          className={cn(
            'w-20 font-semibold cursor-pointer text-sm',
            state.enabled ? 'text-teal-700 dark:text-teal-400' : 'text-muted-foreground',
          )}
        >
          {DAY_LABELS[dow]}
        </Label>

        {state.enabled ? (
          <span className="text-xs text-muted-foreground">
            {state.slots.length} slot{state.slots.length !== 1 ? 's' : ''}
          </span>
        ) : (
          <Badge variant="outline" className="text-[10px] text-muted-foreground border-gray-200">
            Indisponível
          </Badge>
        )}
      </div>

      {/* Slots do dia */}
      {state.enabled && (
        <div className="px-4 pb-3 space-y-2">
          {state.slots.map((slot, idx) => (
            <div key={idx} className="flex items-end gap-2 flex-wrap">
              <TimeSelect
                label="Início"
                value={slot.start_time}
                options={HOUR_OPTIONS}
                onChange={(v) => updateSlot(idx, 'start_time', v)}
              />
              <span className="pb-1.5 text-muted-foreground text-sm">até</span>
              <TimeSelect
                label="Término"
                value={slot.end_time}
                options={HOUR_OPTIONS.filter((h) => h > slot.start_time)}
                onChange={(v) => updateSlot(idx, 'end_time', v)}
              />
              {state.slots.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeSlot(idx)}
                  className="pb-1 text-red-400 hover:text-red-600 transition-colors"
                  aria-label="Remover slot"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addSlot}
            className="inline-flex items-center gap-1.5 text-xs text-teal-600 hover:text-teal-800 font-medium mt-1 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Adicionar horário
          </button>
        </div>
      )}
    </div>
  )
}

// ─── WeeklyAvailabilityEditor ─────────────────────────────────────────────────

export function WeeklyAvailabilityEditor() {
  const { data: instructorId } = useMyInstructorId()

  const { data: existing = [], isLoading } = useInstructorWeeklyAvailability(instructorId)
  const { mutate: saveAvailability, isPending: isSaving } = useSetWeeklyAvailability()

  const [week, setWeek] = useState<WeekState>(defaultWeekState())
  const [dirty, setDirty] = useState(false)

  // Inicializa o estado a partir dos dados existentes
  useEffect(() => {
    if (!isLoading && existing.length >= 0) {
      const state = defaultWeekState()

      for (const row of existing) {
        const dow = row.day_of_week
        if (!state[dow].enabled) {
          state[dow] = { enabled: true, slots: [] }
        }
        state[dow].slots.push({
          start_time: row.start_time.slice(0, 5),
          end_time:   row.end_time.slice(0, 5),
        })
      }

      setWeek(state)
      setDirty(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, existing.length])

  function updateDay(dow: number, state: DayState) {
    setWeek((prev) => ({ ...prev, [dow]: state }))
    setDirty(true)
  }

  function handleSave() {
    const slots: WeeklySlotInput[] = []

    for (let dow = 0; dow <= 6; dow++) {
      const day = week[dow]
      if (!day.enabled) continue

      for (const slot of day.slots) {
        if (slot.end_time <= slot.start_time) continue  // ignora slots inválidos
        slots.push({
          day_of_week: dow,
          start_time:  slot.start_time,
          end_time:    slot.end_time,
        })
      }
    }

    saveAvailability(slots, {
      onSuccess: () => setDirty(false),
    })
  }

  // Resumo compacto: dias ativos
  const activeDays = [0, 1, 2, 3, 4, 5, 6].filter((d) => week[d].enabled)

  return (
    <Card className="dark:bg-gray-900">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-teal-600" />
          Disponibilidade semanal
        </CardTitle>
        <CardDescription>
          Defina os horários recorrentes em que você está disponível para aulas.
          Alunos só poderão agendar dentro desses blocos.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-xl" />
            ))}
          </div>
        ) : (
          <>
            {/* Indicador de dias ativos */}
            {activeDays.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pb-1">
                {activeDays.map((d) => (
                  <Badge
                    key={d}
                    variant="outline"
                    className="text-[10px] border-teal-200 text-teal-700 dark:border-teal-800 dark:text-teal-400"
                  >
                    {DAY_SHORT[d]}
                  </Badge>
                ))}
              </div>
            )}

            {/* Grid semanal */}
            <div className="space-y-2">
              {[1, 2, 3, 4, 5, 6, 0].map((dow) => (
                <DayRow
                  key={dow}
                  dow={dow}
                  state={week[dow]}
                  onChange={(s) => updateDay(dow, s)}
                />
              ))}
            </div>

            {/* Salvar */}
            <div className="flex items-center justify-between pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                {activeDays.length === 0
                  ? 'Nenhum dia ativo. Habilite os dias em que atende.'
                  : `${activeDays.length} dia${activeDays.length > 1 ? 's' : ''} configurado${activeDays.length > 1 ? 's' : ''}.`}
              </p>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={isSaving || !dirty}
                className="bg-teal-600 hover:bg-teal-700"
                data-testid="btn-save-weekly-availability"
              >
                {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {isSaving ? 'Salvando...' : 'Salvar disponibilidade'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
